import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import AsistenteService from '../services/AsistenteService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import UserAccountMenu from './common/UserAccountMenu';
import RegionalService from '../services/RegionalService';
import materiaService from '../services/MateriaService';
import tipoAsistenteService from '../services/TipoAsistenteService';
import ProcesosMenu from './common/ProcesosMenu';
import './NewAssistant.css';
import {
  isGptClassicEnabled as hasGptClassicFeature,
  isOpenAIAssistantEnabled as hasOpenAIAssistantFeature,
  isRagFlowAssistantEnabled as hasRagFlowAssistantFeature
} from '../utils/adminMenu';
import useRegionalFeatureFlags from '../hooks/useRegionalFeatureFlags';
import ProfessorMenuOptions from './sidebar/ProfessorMenuOptions';
import { navigateProfessorMenuItem, NEW_ASSISTANT_MENU_ITEM, NEW_RAGFLOW_ASSISTANT_MENU_ITEM } from './sidebar/menuConfig';
import { findAssistantTypeById, isInstitutionalAssistantType } from '../utils/asistenteTypes';
import {
  buildRagFlowModelLabel,
  getDefaultRagFlowModelId,
  normalizeRagFlowModelsResponse
} from '../utils/ragFlowModels';

const isProfessorUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return roleName === 'profesor' || roleName.includes('profesor') || roleName.includes('profe');
};

const VALIDATION_ERROR_MESSAGE = 'No pudimos procesar los archivos. Verifica que todos sean PDF válidos y que no superen los límites permitidos.';
const PROMPT_FEATURES = [
  'Usa exclusivamente los PDFs adjuntos (RAG obligatorio).',
  'Ejecuta file_search antes de responder consultas académicas.',
  'Si no hay evidencia en los PDFs, responde con el mensaje de falta de información.',
  'Respuestas académicas en HTML con citas de PDF y número de página.',
  'Puede responder saludos o uso del asistente sin citar PDFs.',
  'Imágenes o esquemas solo si existen en PDFs o son representaciones fieles con aviso.',
  'Puede armar planes de estudio y actuar como tutor orientado a ayudar al alumno.'
];

const getFriendlyApiErrorMessage = (error, fallback) => {
  if (!error || !error.message) {
    return fallback;
  }
  const normalized = error.message.toLowerCase();
  if (normalized.includes('validation')) {
    return VALIDATION_ERROR_MESSAGE;
  }
  return error.message;
};

const NewAssistant = ({ provider = 'openai' }) => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isProfesor = isProfessorUser(user);
  const isRagFlowProvider = String(provider).toLowerCase() === 'ragflow';
  const providerLabel = isRagFlowProvider ? 'AVE' : 'OpenAI';
  const { featureFlags, isRefreshingFeatureFlags } = useRegionalFeatureFlags(user, { enabled: !authLoading });
  const isGptClassicEnabled = hasGptClassicFeature(featureFlags);
  const isOpenAIAssistantEnabled = hasOpenAIAssistantFeature(featureFlags);
  const isRagFlowAssistantEnabled = hasRagFlowAssistantFeature(featureFlags);
  const isCurrentAssistantFeatureEnabled = isRagFlowProvider ? isRagFlowAssistantEnabled : isOpenAIAssistantEnabled;
  
  // Estados del componente
  const [isLoading, setIsLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionDeniedReason, setPermissionDeniedReason] = useState('');
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [formData, setFormData] = useState({
    nombre: '',
    nicknameUrl: '',
    descripcion: '',
    promptExtra: '',
    instruccionesInstitucionales: '',
    ragFlowModelo: '',
    idRegional: '',
    idArea: '',
    idMateria: '',
    idTipoAsistente: '',
    archivosPdf: [],
    publico: true,
    passwordAsistente: ''
  });
  const [materias, setMaterias] = useState([]);
  const [materiasLoading, setMateriasLoading] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [tiposAsistente, setTiposAsistente] = useState([]);
  const [tiposLoading, setTiposLoading] = useState(false);
  const [tiposError, setTiposError] = useState('');
  const [ragFlowModels, setRagFlowModels] = useState([]);
  const [ragFlowModelsLoading, setRagFlowModelsLoading] = useState(false);
  const [ragFlowModelsError, setRagFlowModelsError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const materiaEsObligatoria = true;
  const selectedTipoAsistente = useMemo(
    () => findAssistantTypeById(tiposAsistente, formData.idTipoAsistente),
    [tiposAsistente, formData.idTipoAsistente]
  );
  const isInstitutionalTipoSeleccionado = useMemo(
    () => isInstitutionalAssistantType(selectedTipoAsistente || { id: formData.idTipoAsistente }),
    [selectedTipoAsistente, formData.idTipoAsistente]
  );
  const shouldShowPromptSettings = !isRagFlowProvider;
  const shouldShowExtraInstructions = shouldShowPromptSettings || isRagFlowProvider;
  const shouldRequireInstitutionalInstructions = shouldShowPromptSettings && isInstitutionalTipoSeleccionado;
  const selectedRagFlowModelConfig = useMemo(() => {
    if (!formData.ragFlowModelo) {
      return null;
    }

    return ragFlowModels.find((model) => model.id === formData.ragFlowModelo) || null;
  }, [formData.ragFlowModelo, ragFlowModels]);
  const isSubmitDisabled = isSubmitting ||
    formData.archivosPdf.length === 0 ||
    (isRagFlowProvider && (ragFlowModelsLoading || !formData.ragFlowModelo));

  // Validar permisos al montar el componente
  useEffect(() => {
    if (authLoading || isRefreshingFeatureFlags) return;
    validateUserPermissions();
  }, [user, isProfesor, authLoading, isCurrentAssistantFeatureEnabled, isRefreshingFeatureFlags]);

  const validateUserPermissions = () => {
    try {
      if (!isProfesor) {
        setHasPermission(false);
        setPermissionDeniedReason('role');
        setAlert({
          show: true,
          title: 'Acceso restringido',
          message: 'Esta sección está disponible únicamente para usuarios con rol Profesor.',
          type: 'warning'
        });
        setIsLoading(false);
        return;
      }

      // Verificar si hay usuario autenticado
      if (!user) {
        setAlert({
          show: true,
          title: 'Sesión requerida',
          message: 'Debes iniciar sesión para acceder a esta funcionalidad',
          type: 'error'
        });
        setHasPermission(false);
        setPermissionDeniedReason('session');
        setIsLoading(false);
        
        // Redirigir al login después de un breve retraso
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      if (!isCurrentAssistantFeatureEnabled) {
        if (isRagFlowProvider) {
          setHasPermission(false);
          setPermissionDeniedReason('feature');
          setAlert({
            show: true,
            title: 'Funcionalidad deshabilitada',
            message: 'El feature flag de Asistentes AVE no esta habilitado para esta regional.',
            type: 'warning'
          });
          setIsLoading(false);
          return;
        }
        setHasPermission(false);
        setPermissionDeniedReason('feature');
        setAlert({
          show: true,
          title: 'Funcionalidad deshabilitada',
          message: 'El feature flag de Asistentes OpenAI no está habilitado para esta regional.',
          type: 'warning'
        });
        setIsLoading(false);
        return;
      }

      // Verificar permisos desde las propiedades del usuario
      if (user.pertmiteCrearAsistente === true) {
        setPermissionDeniedReason('');
        setHasPermission(true);
        setIsLoading(false);
        return;
      }

      // Si no está en el objeto user, verificar en cookies
      const userDataFromCookie = getUserDataFromCookies();
      
      if (userDataFromCookie && userDataFromCookie.pertmiteCrearAsistente === true) {
        setPermissionDeniedReason('');
        setHasPermission(true);
      } else {
        setHasPermission(false);
        setPermissionDeniedReason('permission');
        setAlert({
          show: true,
          title: 'Permiso denegado',
          message: 'No tienes permisos para crear asistentes. Contacta al administrador.',
          type: 'error'
        });
      }
      
    } catch (error) {
      console.error('Error al validar permisos:', error);
      setHasPermission(false);
      setPermissionDeniedReason('error');
      setAlert({
        show: true,
        title: 'Error de permisos',
        message: 'Error al validar permisos. Inténtalo nuevamente.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isProfesor) return;
    const activeRegionalId = RegionalService.getActiveRegionalId();
    setFormData((prev) => ({ ...prev, idRegional: activeRegionalId || prev.idRegional }));

    const fetchMaterias = async () => {
      setMateriasLoading(true);
      setMateriasError('');
      try {
        const list = activeRegionalId
          ? await materiaService.getMisMaterias({ idRegional: activeRegionalId })
          : await materiaService.getMisMaterias();
        setMaterias(Array.isArray(list) ? list : []);
      } catch (error) {
        setMateriasError(error.message || 'No pudimos cargar tus materias.');
      } finally {
        setMateriasLoading(false);
      }
    };

    const fetchTipos = async () => {
      setTiposLoading(true);
      setTiposError('');
      try {
        const list = await tipoAsistenteService.getTiposActivos();
        setTiposAsistente(Array.isArray(list) ? list : []);
      } catch (error) {
        setTiposError(error.message || 'No pudimos cargar los tipos de asistente.');
      } finally {
        setTiposLoading(false);
      }
    };

    fetchMaterias();
    fetchTipos();
  }, [isProfesor]);

  useEffect(() => {
    if (!isRagFlowProvider || !hasPermission) {
      return;
    }

    let cancelled = false;
    const fetchRagFlowModels = async () => {
      setRagFlowModelsLoading(true);
      setRagFlowModelsError('');
      try {
        const activeRegionalId = formData.idRegional || RegionalService.getActiveRegionalId();
        const response = await AsistenteService.getRagFlowModelos(activeRegionalId);
        if (cancelled) {
          return;
        }

        const models = normalizeRagFlowModelsResponse(response);
        setRagFlowModels(models);
        const defaultModelId = getDefaultRagFlowModelId(models);
        setFormData((prev) => {
          if (!defaultModelId) {
            return prev;
          }

          const currentExists = models.some((model) => model.id === prev.ragFlowModelo);
          return currentExists ? prev : { ...prev, ragFlowModelo: defaultModelId };
        });
      } catch (error) {
        if (!cancelled) {
          setRagFlowModels([]);
          setRagFlowModelsError(error.message || 'No pudimos cargar los modelos de RagFlow.');
        }
      } finally {
        if (!cancelled) {
          setRagFlowModelsLoading(false);
        }
      }
    };

    fetchRagFlowModels();

    return () => {
      cancelled = true;
    };
  }, [isRagFlowProvider, hasPermission, formData.idRegional]);

  // Función auxiliar para obtener datos del usuario desde cookies
  const getUserDataFromCookies = () => {
    try {
      const cookies = document.cookie.split(';');
      
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        
        if (name === 'user_data' && value) {
          const decodedValue = decodeURIComponent(value);
          return JSON.parse(decodedValue);
        }
        
        // Fallback para cookies anteriores
        if (name === 'authUser' && value) {
          const decodedValue = decodeURIComponent(value);
          return JSON.parse(decodedValue);
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error al parsear datos de usuario desde cookies:', error);
      return null;
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTipoAsistenteChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      idTipoAsistente: value
    }));
  };

  const handleMateriaChange = (value) => {
    const selected = materias.find((m) => (m.id || m.Id) === value);
    const idArea = selected?.idArea || selected?.IdArea || '';
    const idRegional = selected?.idRegional || selected?.IdRegional || formData.idRegional || '';
    setFormData((prev) => ({
      ...prev,
      idMateria: value,
      idArea: value ? idArea : '',
      idRegional
    }));
  };

  const handleNicknameChange = (value) => {
    const normalized = value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-');
    handleInputChange('nicknameUrl', normalized);
  };

  // Manejar drag and drop de archivos
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  const addFiles = (newFiles) => {
    // Filtrar solo archivos PDF
    const pdfFiles = newFiles.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== newFiles.length) {
      setAlert({
        show: true,
        title: 'Archivos inválidos',
        message: 'Solo se permiten archivos PDF',
        type: 'warning'
      });
    }

    setFormData(prev => {
      const currentFiles = prev.archivosPdf;
      const totalFiles = [...currentFiles, ...pdfFiles];
      
      // Validar máximo 20 archivos
      if (totalFiles.length > 20) {
        setAlert({
          show: true,
          title: 'Límite de archivos',
          message: 'No se pueden subir más de 20 archivos PDF',
          type: 'error'
        });
        return prev; // No actualizar si excedel límite
      }
      
      return {
        ...prev,
        archivosPdf: totalFiles
      };
    });
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      archivosPdf: prev.archivosPdf.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.nombre.trim()) {
      setAlert({
        show: true,
        title: 'Nombre requerido',
        message: 'El nombre del asistente es obligatorio',
        type: 'error'
      });
      return;
    }

    if (materiaEsObligatoria && materiasLoading) {
      setAlert({
        show: true,
        title: 'Materias cargando',
        message: 'Esperá a que carguen tus materias para continuar.',
        type: 'warning'
      });
      return;
    }

    if (materiaEsObligatoria && materias.length === 0) {
      setAlert({
        show: true,
        title: 'Sin materias asignadas',
        message: 'No tienes materias asignadas para asociar al asistente.',
        type: 'warning'
      });
      return;
    }

    if (materiaEsObligatoria && !formData.idMateria) {
      setAlert({
        show: true,
        title: 'Materia requerida',
        message: 'Selecciona una materia asignada para el asistente.',
        type: 'warning'
      });
      return;
    }

    if (shouldRequireInstitutionalInstructions && !formData.instruccionesInstitucionales.trim()) {
      setAlert({
        show: true,
        title: 'Instrucciones requeridas',
        message: 'Debes ingresar las instrucciones del asistente institucional.',
        type: 'warning'
      });
      return;
    }

    if (isRagFlowProvider && !formData.ragFlowModelo.trim()) {
      setAlert({
        show: true,
        title: 'Modelo requerido',
        message: 'Selecciona un modelo disponible en RagFlow para crear el asistente AVE.',
        type: 'warning'
      });
      return;
    }

    if (formData.archivosPdf.length === 0) {
      setAlert({
        show: true,
        title: 'Archivos requeridos',
        message: 'Debes subir al menos un archivo PDF',
        type: 'error'
      });
      return;
    }

    if (formData.archivosPdf.length > 20) {
      setAlert({
        show: true,
        title: 'Límite de archivos',
        message: 'No puedes subir más de 20 archivos PDF',
        type: 'error'
      });
      return;
    }
    if (!formData.publico && !formData.passwordAsistente.trim()) {
      setAlert({
        show: true,
        title: 'Password requerido',
        message: 'Debes ingresar un password cuando el asistente es privado.',
        type: 'error'
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Mostrar mensaje inicial
      setAlert({
        show: true,
        title: 'Creando asistente',
        message: 'Creando asistente... Este proceso puede demorar unos minutos debido a los archivos que se deben procesar.',
        type: 'info'
      });
      
      // Crear FormData para envío multipart
      const formDataToSend = new FormData();
      formDataToSend.append('nombre', formData.nombre);
      if (formData.nicknameUrl.trim()) {
        formDataToSend.append('nicknameUrl', formData.nicknameUrl.trim());
      }
      if (formData.idRegional) {
        formDataToSend.append('idRegional', formData.idRegional);
      }
      if (formData.idArea) {
        formDataToSend.append('idArea', formData.idArea);
      }
      if (formData.idMateria) {
        formDataToSend.append('idMateria', formData.idMateria);
      }
      if (formData.idTipoAsistente) {
        formDataToSend.append('idTipoAsistente', formData.idTipoAsistente);
      }
      if (isRagFlowProvider && formData.ragFlowModelo.trim()) {
        formDataToSend.append('ragFlowModelo', formData.ragFlowModelo.trim());
      }
      
      // Agregar todos los archivos PDF
      formData.archivosPdf.forEach(file => {
        formDataToSend.append('archivosPdf', file);
      });
      if (formData.descripcion) {
        formDataToSend.append('descripcion', formData.descripcion);
      }
      if (shouldShowExtraInstructions && formData.promptExtra.trim()) {
        formDataToSend.append('promptExtra', formData.promptExtra.trim());
      }
      if (shouldShowPromptSettings && isInstitutionalTipoSeleccionado && formData.instruccionesInstitucionales.trim()) {
        formDataToSend.append('instruccionesInstitucionales', formData.instruccionesInstitucionales.trim());
      }
      formDataToSend.append('publico', String(formData.publico));
      formDataToSend.append('conPassword', String(!formData.publico));
      if (!formData.publico && formData.passwordAsistente.trim()) {
        formDataToSend.append('passwordAsistente', formData.passwordAsistente.trim());
      }

      // Hacer la petición POST
      const response = await AsistenteService.crearAsistente(formDataToSend, provider);
      const creationQueued = Boolean(response?.encolado);
      
      setAlert({
        show: true,
        title: creationQueued ? 'Asistente AVE encolado' : 'Asistente creado',
        message: creationQueued
          ? (response?.mensaje || 'creación del asistente AVE encolado, verifique el estado de creacion en la sección "Procesos".')
          : 'Asistente creado exitosamente',
        type: 'success'
      });
      
      // Limpiar formulario después del éxito
      setTimeout(() => {
        setFormData({
          nombre: '',
          nicknameUrl: '',
          descripcion: '',
          promptExtra: '',
          instruccionesInstitucionales: '',
          ragFlowModelo: getDefaultRagFlowModelId(ragFlowModels),
          idRegional: RegionalService.getActiveRegionalId() || '',
          idArea: '',
          idMateria: '',
          idTipoAsistente: '',
          archivosPdf: [],
          publico: true,
          passwordAsistente: ''
        });

        if (creationQueued) {
          navigate('/procesos');
        }
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error al crear asistente:', error);
      setAlert({
        show: true,
        title: 'Error al crear asistente',
        message: getFriendlyApiErrorMessage(error, 'No pudimos crear el asistente. Inténtalo nuevamente.'),
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const handleGoBack = () => {
    navigate('/dashboard');
  };

  const handleProfessorMenuSelect = (item) => {
    navigateProfessorMenuItem(navigate, item.id);
  };

  // Mostrar loading mientras se validan permisos
  if (authLoading || isLoading || isRefreshingFeatureFlags) {
    return (
      <div className="new-assistant-container">
        <div className="loading-container">
          <LoadingSpinner message="Validando permisos..." />
        </div>
      </div>
    );
  }

  // Redirigir al dashboard si no tiene permisos (mostrar alerta allí)
  if (!hasPermission) {
    navigate('/dashboard', {
      replace: true,
      state: permissionDeniedReason === 'feature'
        ? (isRagFlowProvider ? { deniedRagFlowAssistantFeature: true } : { deniedOpenAIAssistantFeature: true })
        : { deniedCreateAssistant: true }
    });
    return null;
  }

  // Mostrar formulario de creación si tiene permisos
  return (
    <div className="new-dashboard">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          <ProfessorMenuOptions
            user={user}
            isGptClassicEnabled={isGptClassicEnabled}
            isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
            isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
            extraItems={[isRagFlowProvider ? NEW_RAGFLOW_ASSISTANT_MENU_ITEM : NEW_ASSISTANT_MENU_ITEM]}
            activeItemId={isRagFlowProvider ? 'nuevo-ragflow-asistente' : 'nuevo-asistente'}
            onSelect={handleProfessorMenuSelect}
          />
        </nav>
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.rol || user?.role || 'Profesor'}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">
              <i className="fas fa-robot"></i>
              Crear Nuevo Asistente {providerLabel}
            </h1>
            <p className="page-subtitle">Sube archivos PDF para crear un asistente personalizado con {providerLabel}</p>
          </div>
          
          <div className="header-right">
            <ActiveRegionalBadge />
            <ProcesosMenu />
            <UserAccountMenu />
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          <div className="new-assistant-content">
            <form onSubmit={handleSubmit} className="assistant-form">
              <div className="assistant-form-grid">
                <div className="assistant-form-panel">
              {/* Información básica */}
              <div className="form-section basic-info-section">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-robot"></i>
                  </div>
                  <div className="section-text">
                    <h3 className="section-title">Información del Asistente</h3>
                    <p className="section-description">Define el nombre y características básicas de tu asistente IA</p>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="nombre" className="form-label">
                    <i className="fas fa-tag"></i>
                    Nombre del Asistente *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    className="form-input enhanced"
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    placeholder="Ej: Asistente de Lógica y Estructuras Discretas"
                    required
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-lightbulb"></i>
                    Escoge un nombre descriptivo que refleje el área de conocimiento
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="idTipoAsistente" className="form-label">
                    <i className="fas fa-layer-group"></i>
                    Tipo de asistente
                  </label>
                  <select
                    id="idTipoAsistente"
                    className="form-select enhanced"
                    value={formData.idTipoAsistente}
                    onChange={(e) => handleTipoAsistenteChange(e.target.value)}
                    disabled={isSubmitting || tiposLoading}
                  >
                    <option value="">Selecciona un tipo</option>
                    {tiposAsistente.map((tipo) => {
                      const id = tipo.id || tipo.Id;
                      const nombre = tipo.nombre || tipo.Nombre || 'Tipo';
                      return (
                        <option key={id} value={id}>
                          {nombre}
                        </option>
                      );
                    })}
                  </select>
                  {tiposLoading && (
                    <div className="input-hint">
                      <i className="fas fa-spinner fa-spin"></i>
                      Cargando tipos de asistente...
                    </div>
                  )}
                  {tiposError && (
                    <div className="input-hint" style={{ color: '#dc2626' }}>
                      <i className="fas fa-triangle-exclamation"></i>
                      {tiposError}
                    </div>
                  )}
                </div>

                {isRagFlowProvider && (
                  <div className="form-group">
                    <label htmlFor="ragFlowModelo" className="form-label">
                      <i className="fas fa-microchip"></i>
                      Modelo AVE *
                    </label>
                    <select
                      id="ragFlowModelo"
                      className="form-select enhanced"
                      value={formData.ragFlowModelo}
                      onChange={(e) => handleInputChange('ragFlowModelo', e.target.value)}
                      disabled={isSubmitting || ragFlowModelsLoading}
                      required
                    >
                      <option value="">
                        {ragFlowModelsLoading ? 'Cargando modelos...' : 'Selecciona un modelo'}
                      </option>
                      {ragFlowModels.map((modelo) => (
                        <option key={modelo.id} value={modelo.id}>
                          {buildRagFlowModelLabel(modelo)}{modelo.esDefault ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                    {ragFlowModelsError && (
                      <div className="input-hint" style={{ color: '#dc2626' }}>
                        <i className="fas fa-triangle-exclamation"></i>
                        {ragFlowModelsError}
                      </div>
                    )}
                    {selectedRagFlowModelConfig && (
                      <div className="model-characteristics-card">
                        <div className="model-characteristics-header">
                          <i className="fas fa-circle-info"></i>
                          <span>{buildRagFlowModelLabel(selectedRagFlowModelConfig)}</span>
                        </div>
                        {Array.isArray(selectedRagFlowModelConfig.caracteristicas) && selectedRagFlowModelConfig.caracteristicas.length > 0 && (
                          <ul className="model-characteristics-list">
                            {selectedRagFlowModelConfig.caracteristicas.map((caracteristica, index) => (
                              <li key={`${selectedRagFlowModelConfig.id}-${index}`}>{caracteristica}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="idMateria" className="form-label">
                    <i className="fas fa-book"></i>
                    Materia asignada *
                  </label>
                  <select
                    id="idMateria"
                    className="form-select enhanced"
                    value={formData.idMateria}
                    onChange={(e) => handleMateriaChange(e.target.value)}
                    disabled={isSubmitting || materiasLoading}
                    required
                  >
                    <option value="">Selecciona una materia</option>
                    {materias.map((materia) => {
                      const id = materia.id || materia.Id;
                      const nombre = materia.nombre || materia.Nombre || '';
                      const codigo = materia.codigo || materia.Codigo || '';
                      const label = nombre || codigo || 'Materia';
                      return (
                        <option key={id} value={id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                  {materiasLoading && (
                    <div className="input-hint">
                      <i className="fas fa-spinner fa-spin"></i>
                      Cargando materias asignadas...
                    </div>
                  )}
                  {materiasError && (
                    <div className="input-hint" style={{ color: '#dc2626' }}>
                      <i className="fas fa-triangle-exclamation"></i>
                      {materiasError}
                    </div>
                  )}
                  {materiaEsObligatoria && !materiasLoading && !materiasError && materias.length === 0 && (
                    <div className="input-hint" style={{ color: '#b45309' }}>
                      <i className="fas fa-circle-info"></i>
                      No tienes materias asignadas para asociar.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="nicknameUrl" className="form-label">
                    <i className="fas fa-link"></i>
                    Nickname para URL
                  </label>
                  <input
                    type="text"
                    id="nicknameUrl"
                    className="form-input enhanced"
                    value={formData.nicknameUrl}
                    onChange={(e) => handleNicknameChange(e.target.value)}
                    placeholder="ej: algebra-lineal-1"
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-info-circle"></i>
                    Solo letras, números y guiones. Se usará para compartir el acceso.
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="descripcion" className="form-label">
                    <i className="fas fa-align-left"></i>
                    Descripción del Asistente
                  </label>
                  <textarea
                    id="descripcion"
                    className="form-input enhanced"
                    rows="4"
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    placeholder="Describe brevemente el propósito o alcance del asistente"
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-info-circle"></i>
                    Esto ayuda a identificar rápidamente el objetivo del asistente
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-eye"></i>
                    Visibilidad y acceso
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.publico}
                        onChange={(e) => {
                          const nextValue = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            publico: nextValue,
                            passwordAsistente: nextValue ? '' : prev.passwordAsistente
                          }));
                        }}
                        disabled={isSubmitting}
                      />
                      <span>Asistente público</span>
                    </label>
                  </div>
                </div>

                {!formData.publico && (
                  <div className="form-group">
                    <label htmlFor="passwordAsistente" className="form-label">
                      <i className="fas fa-key"></i>
                      Password del Asistente
                    </label>
                    <input
                      id="passwordAsistente"
                      type="text"
                      className="form-input enhanced"
                      value={formData.passwordAsistente}
                      onChange={(e) => handleInputChange('passwordAsistente', e.target.value)}
                      placeholder="Ingresa el password (obligatorio)"
                      disabled={isSubmitting}
                    />
                  </div>
                )}
              </div>

              {/* Archivos PDF */}
              <div className="form-section files-section">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-file-pdf"></i>
                  </div>
                  <div className="section-text">
                    <h3 className="section-title">Documentos de Conocimiento</h3>
                    <p className="section-description">Sube los archivos PDF que contengan la información que tu asistente debe conocer</p>
                  </div>
                </div>
                
                <div className="files-upload-container">
                  <div 
                    className={`file-dropzone ${isDragOver ? 'dragover' : ''} ${formData.archivosPdf.length > 0 ? 'has-files' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="dropzone-border">
                      <div className="dropzone-content">
                        <div className="dropzone-icon">
                          <i className="fas fa-cloud-upload-alt"></i>
                        </div>
                        <div className="dropzone-text">
                          <h4>Arrastra y suelta tus archivos PDF aquí</h4>
                          <p>Los archivos se procesarán automáticamente para entrenar a tu asistente</p>
                        </div>
                        <div className="dropzone-divider">
                          <span>o</span>
                        </div>
                        <label className="file-input-label">
                          <input
                            type="file"
                            multiple
                            accept=".pdf"
                            onChange={handleFileInput}
                            disabled={isSubmitting}
                            style={{ display: 'none' }}
                          />
                          <span className="btn btn-upload">
                            <i className="fas fa-folder-open"></i>
                            Seleccionar Archivos PDF
                          </span>
                        </label>
                        <div className="upload-limits">
                          <div className="limit-item">
                            <i className="fas fa-file-alt"></i>
                            <span>Máximo 20 archivos</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-file-pdf"></i>
                            <span>Solo formato PDF</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-weight-hanging"></i>
                            <span>Hasta 10MB por archivo</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lista de archivos seleccionados */}
                {formData.archivosPdf.length > 0 && (
                  <div className="selected-files">
                    <h4>Archivos seleccionados ({formData.archivosPdf.length}/20):</h4>
                    <div className="files-list">
                      {formData.archivosPdf.map((file, index) => (
                        <div key={index} className="file-item">
                          <div className="file-info">
                            <i className="fas fa-file-pdf"></i>
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                          <button
                            type="button"
                            className="remove-file-btn"
                            onClick={() => removeFile(index)}
                            disabled={isSubmitting}
                            title="Eliminar archivo"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {shouldShowExtraInstructions && (
                  <div className="form-section prompt-settings-section">
                    <div className="section-header">
                      <div className="section-icon">
                        <i className="fas fa-scroll"></i>
                      </div>
                      <div className="section-text">
                        <h3 className="section-title">
                          {isRagFlowProvider ? 'Aclaraciones extra' : 'Instrucciones y aclaraciones'}
                        </h3>
                        <p className="section-description">
                          {isRagFlowProvider
                            ? 'Estas aclaraciones se agregan al final del prompt generado para el asistente AVE.'
                            : 'Configura el comportamiento específico del asistente después de definir sus archivos de conocimiento.'}
                        </p>
                      </div>
                    </div>

                    {shouldShowPromptSettings && isInstitutionalTipoSeleccionado && (
                      <div className="prompt-extra-field">
                        <label htmlFor="instruccionesInstitucionales" className="form-label">
                          <i className="fas fa-scroll"></i>
                          Instrucciones del asistente institucional *
                        </label>
                        <textarea
                          id="instruccionesInstitucionales"
                          className="form-input enhanced"
                          rows="8"
                          value={formData.instruccionesInstitucionales}
                          onChange={(e) => handleInputChange('instruccionesInstitucionales', e.target.value)}
                          placeholder="Describe el comportamiento, alcance, tono y reglas específicas que debe seguir este asistente institucional."
                          disabled={isSubmitting}
                        />
                        <div className="input-hint">
                          <i className="fas fa-info-circle"></i>
                          Este bloque se usará como cuerpo principal del prompt institucional, manteniendo la configuración operativa obligatoria antes del prompt extra.
                        </div>
                      </div>
                    )}

                    <div className="prompt-extra-field">
                      <label htmlFor="promptExtra" className="form-label">
                        <i className="fas fa-pen"></i>
                        Aclaraciones extra (opcional)
                      </label>
                      <textarea
                        id="promptExtra"
                        className="form-input enhanced"
                        rows="6"
                        value={formData.promptExtra}
                        onChange={(e) => handleInputChange('promptExtra', e.target.value)}
                        placeholder="Ej: prioriza ejemplos resueltos, responde en español neutro..."
                        disabled={isSubmitting}
                      />
                      <div className="input-hint">
                        <i className="fas fa-info-circle"></i>
                        Estas reglas se agregarán al prompt base del asistente.
                      </div>
                    </div>
                  </div>
                )}
              </div>

                </div>

                {shouldShowPromptSettings && !isInstitutionalTipoSeleccionado && (
                  <aside className="assistant-prompt-panel">
                    <div className="prompt-panel-header">
                      <i className="fas fa-clipboard-list"></i>
                      <div>
                        <h3>Comportamiento del asistente</h3>
                        <p>Resumen del prompt base configurado en el backend.</p>
                      </div>
                    </div>
                    <ul className="prompt-features-list">
                      {PROMPT_FEATURES.map((item) => (
                        <li key={item}>
                          <i className="fas fa-check-circle"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </aside>
                )}
              </div>

              {/* Botones de Acción */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleGoBack}
                  disabled={isSubmitting}
                >
                  <i className="fas fa-arrow-left"></i>
                  Volver al Dashboard
                </button>
                
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitDisabled}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Creando Asistente...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-robot"></i>
                      Crear Asistente
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewAssistant;
